import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DialogModule } from 'primeng/dialog';

/**
 * NumericKeypadDialogComponent
 * ─────────────────────────────────────────────────────────────────────────────
 * Centered, touch-friendly numeric entry modal. Opens with a starting value,
 * lets the user compose a new value via an on-screen keypad (with dedicated
 * Clear / Backspace / Close actions), and only commits the value back to the
 * caller when "OK" is pressed — closing any other way (X, backdrop, Escape)
 * discards the edit.
 *
 * Deliberately generic (label/unit/max are all inputs) so any numeric field
 * — not just restaurant discounts — can reuse it later without duplicating
 * this component.
 * ─────────────────────────────────────────────────────────────────────────────
 */
@Component({
  selector: 'app-numeric-keypad-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule],
  templateUrl: './numeric-keypad-dialog.component.html',
  styleUrl: './numeric-keypad-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumericKeypadDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() label = 'Enter Value';
  @Input() subLabel = '';
  @Input() unit = '';
  @Input() initialValue: number | string = 0;
  @Input() maxValue: number | null = null;
  @Input() allowDecimal = true;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<number>();
  @Output() cancel = new EventEmitter<void>();

  buffer = '0';

  private static readonly MAX_LENGTH = 8;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      const initial = Number(this.initialValue);
      this.buffer = !this.initialValue || isNaN(initial) || initial <= 0
        ? '0'
        : String(initial);
    }
  }

  get displayValue(): string {
    return this.buffer;
  }

  onDigit(digit: string): void {
    if (digit === '.') {
      if (!this.allowDecimal || this.buffer.includes('.')) {
        return;
      }
      this.buffer = this.buffer + '.';
      return;
    }

    if (this.buffer.length >= NumericKeypadDialogComponent.MAX_LENGTH) {
      return;
    }

    this.buffer = this.buffer === '0' ? digit : this.buffer + digit;
  }

  onBackspace(): void {
    this.buffer = this.buffer.length > 1 ? this.buffer.slice(0, -1) : '0';
  }

  onClear(): void {
    this.buffer = '0';
  }

  onConfirmClick(): void {
    const parsed = parseFloat(this.buffer) || 0;
    const finalValue =
      this.maxValue != null ? Math.min(parsed, this.maxValue) : parsed;
    this.confirm.emit(finalValue);
    this.visibleChange.emit(false);
  }

  onCancelClick(): void {
    this.cancel.emit();
    this.visibleChange.emit(false);
  }

  // Fires when PrimeNG closes the dialog itself (backdrop click / Escape) —
  // treat that exactly like the X button: discard, don't commit.
  handlePrimeNgVisibleChange(isVisible: boolean): void {
    if (!isVisible) {
      this.onCancelClick();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key >= '0' && event.key <= '9') {
      event.preventDefault();
      this.onDigit(event.key);
    } else if (event.key === '.' || event.key === ',') {
      event.preventDefault();
      this.onDigit('.');
    } else if (event.key === 'Backspace') {
      event.preventDefault();
      this.onBackspace();
    } else if (event.key === 'Delete') {
      event.preventDefault();
      this.onClear();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.onConfirmClick();
    }
    // Escape is left to PrimeNG's closeOnEscape → handlePrimeNgVisibleChange.
  }
}
